import React, { useMemo, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const KINDS = [
  { id: 'entered', label: 'Typed on this test' },
  { id: 'test', label: 'A test on this report' },
  { id: 'previous', label: "Last report's reading" },
];

// Sets where each input of a calculated test comes from on this system: typed
// on the test, another test's reading on the report (on any system), or that
// test's reading on the last submitted report. The choice is saved for the
// system, so it applies to every report on the account.
// onSave(sources) returns a promise that rejects with the server's message.
const CalcSourcesEditor = ({ test, report, component, onSave, close }) => {
  const [sources, setSources] = useState(() => Object.fromEntries(
    (test.calc?.inputs || []).map((input) => [input.key, {
      source: input.source || 'entered',
      component_id: input.component_id ? `${input.component_id}` : `${component?.id ?? ''}`,
      test_field_id: input.test_field_id ? `${input.test_field_id}` : '',
    }])
  ));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The report's systems and the tests on each, as the options to link to.
  const systems = useMemo(() => (
    Object.values(report?.components || {})
      .filter((c) => Number(c.id) > 0 && !c.deleted)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((c) => ({
        id: `${c.id}`,
        name: c.name,
        tests: Object.values(c.tests || {})
          .filter((t) => !t.deleted && t.testId !== undefined && `${t.id}` !== `${test.id}`)
          .map((t) => ({ id: `${t.testId}`, name: t.test_name }))
          .sort((a, b) => `${a.name}`.localeCompare(`${b.name}`)),
      }))
  ), [report, test.id]);

  const update = (key, changes) => setSources((current) => ({ ...current, [key]: { ...current[key], ...changes } }));

  const incomplete = Object.values(sources).some((s) => s.source !== 'entered' && (!s.component_id || !s.test_field_id));

  const save = () => {
    if (saving || incomplete) return;
    setSaving(true);
    setError(null);
    onSave(sources)
      .then(close)
      .catch((err) => {
        setError(err.message);
        setSaving(false);
      });
  };

  return (
    <div className="test-panel calc-sources" onClick={(e) => e.stopPropagation()}>
      <span className="test-panel-kicker">Where each input comes from, for this system on every report</span>

      {(test.calc?.inputs || []).map((input) => {
        const chosen = sources[input.key];
        const system = systems.find((s) => s.id === chosen.component_id);
        // Keep the saved test selectable even if it isn't on this report.
        const tests = system?.tests || [];
        const savedMissing = chosen.test_field_id && !tests.some((t) => t.id === chosen.test_field_id);

        return (
          <div key={input.key} className="calc-source">
            <span className="calc-source-label">{input.label}</span>
            <select
              className="browser-default"
              aria-label={`Source for ${input.label}`}
              value={chosen.source}
              onChange={(e) => update(input.key, { source: e.target.value })}
            >
              {KINDS.map((kind) => <option key={kind.id} value={kind.id}>{kind.label}</option>)}
            </select>

            {chosen.source !== 'entered' ? (
              <>
                <select
                  className="browser-default"
                  aria-label={`System for ${input.label}`}
                  value={chosen.component_id}
                  onChange={(e) => update(input.key, { component_id: e.target.value, test_field_id: '' })}
                >
                  <option value="">Choose a system…</option>
                  {systems.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select
                  className="browser-default"
                  aria-label={`Test for ${input.label}`}
                  value={chosen.test_field_id}
                  onChange={(e) => update(input.key, { test_field_id: e.target.value })}
                >
                  <option value="">Choose a test…</option>
                  {savedMissing ? <option value={chosen.test_field_id}>{input.from || 'Current test'}</option> : null}
                  {tests.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </>
            ) : null}
          </div>
        );
      })}

      {error ? <div className="calc-sources-error">{error}</div> : null}

      <div className="calc-sources-actions">
        <button type="button" className="calc-sources-btn" disabled={saving} onClick={close}>
          <CloseIcon /> Cancel
        </button>
        <button type="button" className="calc-sources-btn primary" disabled={saving || incomplete} onClick={save}>
          <CheckIcon /> {saving ? 'Saving…' : 'Save inputs'}
        </button>
      </div>
    </div>
  );
};

export default CalcSourcesEditor;
